"use client"

import { useState, useEffect } from "react"
import { User, Lock, AlertCircle, ArrowLeft, Eye, EyeOff } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { loginUser } from "../../services/loginService"
import logoImage from "../../assets/logo.png"
import carImage from "../../assets/parking.jpg"
import "./Login.css"

export default function Login() {
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [usernameFocused, setUsernameFocused] = useState(false)
  const [passwordFocused, setPasswordFocused] = useState(false)
  const [loginError, setLoginError] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const navigate = useNavigate()

  // Estados para controlar las animaciones
  const [pageLoaded, setPageLoaded] = useState(false)
  const [titleLoaded, setTitleLoaded] = useState(false)
  const [formLoaded, setFormLoaded] = useState(false)

  useEffect(() => {
    // Mostrar mensaje si la sesión expiró
    const expired = localStorage.getItem("sessionExpired")
    if (expired === "true") {
      setLoginError("Tu sesión ha expirado. Por favor inicia sesión nuevamente.")
      localStorage.removeItem("sessionExpired")
    }

    // Animación secuencial de carga
    setTimeout(() => {
      setPageLoaded(true)
      setTimeout(() => {
        setTitleLoaded(true)
        setTimeout(() => {
          setFormLoaded(true)
        }, 300)
      }, 400)
    }, 100)

    // Redirigir si se dejó marcado el login anterior
    const shouldNavigate = localStorage.getItem("shouldNavigateToHome")
    if (shouldNavigate === "true") {
      localStorage.removeItem("shouldNavigateToHome")
      navigate("/home")
    }
  }, [navigate])

  // Enviar formulario de login
  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoginError("")

    try {
      const data = await loginUser(username, password)
      localStorage.setItem("token", data.jwt)
      localStorage.setItem("idEmpleado", data.idEmpleado)
      localStorage.setItem("nombreEmpleado", data.nombreEmpleado)
      localStorage.setItem("role", data.role)
      localStorage.setItem("idsucursal", data.idsucursal)
      localStorage.setItem("idempresa", data.idempresa)

      localStorage.setItem("shouldNavigateToHome", "true")
      window.location.reload()
    } catch (error) {
      console.error("Error al iniciar sesión:", error)
      setLoginError(error.message || "Usuario o contraseña incorrectos. Por favor, intente nuevamente.")
    }
  }

  // Cuando el usuario hace foco en el input
  const handleInputFocus = (field) => {
    setLoginError("")
    if (field === "username") setUsernameFocused(true)
    if (field === "password") setPasswordFocused(true)
  }

  // 👁 Alternar visibilidad de contraseña
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  // ⬅ Volver a pantalla principal
  const navigateToHome = () => {
    navigate("/")
  }

  return (
    <div className={`login-container-login ${pageLoaded ? "page-loaded-login" : ""}`}>
      <div className="login-image-login">
        <img src={carImage || "/placeholder.svg"} alt="Parking Club" className="background-image-login" />
        <div className="overlay-login"></div>
      </div>

      <div className="login-form-container-login">
        {/* Botón para regresar */}
        <button className="home-button-login" onClick={navigateToHome} aria-label="Regresar a la página principal">
          <ArrowLeft size={20} />
        </button>

        <div className="login-form-content-login">
          {/* Logo y título */}
          <div className={`login-logo-login ${titleLoaded ? "title-loaded-login" : ""}`}>
            <img src={logoImage || "/placeholder.svg"} alt="Parking Club Logo" className="logo-image-login" />
            <div className="logo-text-login" translate="no">
              <div className="title-animation-container-login">
                <span className="logo-parking-login">Parking</span>
                <span className="logo-club-login">Club</span>
              </div>
            </div>
          </div>

          {/* Subtítulo */}
          <p className={`login-subtitle-login ${titleLoaded ? "title-loaded-login" : ""}`}>Ingresa a la Aplicación</p>

          {/* Formulario de login */}
          <form onSubmit={handleSubmit} className={formLoaded ? "form-loaded-login" : ""}>
            {/* Mensaje de error */}
            <div className="error-container-login">
              {loginError && (
                <div className="error-message-login">
                  <AlertCircle size={16} />
                  <span>{loginError}</span>
                </div>
              )}
            </div>

            {/* Usuario */}
            <div className={`form-group-login ${usernameFocused || username ? "focused-login" : ""}`}>
              <div className="input-icon-login">
                <User size={20} />
              </div>
              <div className="input-container-login">
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onFocus={() => handleInputFocus("username")}
                  onBlur={() => setUsernameFocused(false)}
                  placeholder="Usuario"
                  required
                />
              </div>
            </div>

            {/* Contraseña */}
            <div className={`form-group-login ${passwordFocused || password ? "focused-login" : ""}`}>
              <div className="input-icon-login">
                <Lock size={20} />
              </div>
              <div className="input-container-login">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => handleInputFocus("password")}
                  onBlur={() => setPasswordFocused(false)}
                  placeholder="Contraseña"
                  required
                />
              </div>
              <button
                type="button"
                className="password-toggle-login"
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {/* Botón de enviar */}
            <button type="submit" className="login-button-login">
              Ingresar
            </button>

            {/* Enlace adicional */}
            <div className="signup-link-login">
              ¿No tienes una cuenta? <a href="/">¡Contáctanos Aquí!</a>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
